import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "cameraInput",
    "captureButton",
    "completeButton",
    "preview",
    "previewPlaceholder",
    "status",
    "statusSpinner",
    "statusIcon",
    "progress",
    "dragDropArea",
  ];
  static values = {
    establishmentId: String,
    documentType: String,
    apiBaseUrl: String,
  };

  connect() {
    this.capturedPhotos = [];
    this.currentSession = null;
    this.startNewSession();
  }

  disconnect() {
    // Clean up any ongoing operations if needed
  }

  async startNewSession() {
    try {
      this.showStatus("Création de la session...");

      const response = await fetch(
        `${this.apiBaseUrlValue}/establishments/${this.establishmentIdValue}/document_upload_sessions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            document_type: this.documentTypeValue,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        this.currentSession = data;
        this.sessionIdValue = data.session_id;
        this.updateUI();
        this.showStatus("Prêt", "success");
      } else {
        throw new Error("Failed to create session");
      }
    } catch (error) {
      console.error("Error starting session:", error);
      this.showStatus("Erreur lors de la création de la session", "error");
    }
  }

  openCamera() {
    if (!this.currentSession) return;
    this.cameraInputTarget.click();
  }

  openFileDialog() {
    if (!this.currentSession) return;
    this.cameraInputTarget.click();
  }

  handleFileSelection(event) {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;
    event.target.value = ""; // Clear input
    this.handleFiles(files);
  }

  async handleFiles(files) {
    try {
      this.showStatus(`Upload de ${files.length} photo${files.length > 1 ? 's' : ''}...`);
      
      // Upload photos sequentially to avoid race conditions
      for (const file of files) {
        await this.uploadPhoto(file);
      }
      
      this.showStatus("Toutes les photos ont été ajoutées", "success");
    } catch (error) {
      console.error("Error handling files:", error);
      this.showStatus("Erreur lors de l'upload", "error");
    }
  }

  handleDragOver(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.hasDragDropAreaTarget) {
      this.dragDropAreaTarget.classList.add("border-primary", "bg-primary/5");
    }
  }

  handleDragLeave(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.hasDragDropAreaTarget) {
      this.dragDropAreaTarget.classList.remove(
        "border-primary",
        "bg-primary/5"
      );
    }
  }

  handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.hasDragDropAreaTarget) {
      this.dragDropAreaTarget.classList.remove(
        "border-primary",
        "bg-primary/5"
      );
    }
    if (!this.currentSession) return;
    const files = Array.from(event.dataTransfer.files).filter((file) =>
      file.type.startsWith("image/")
    );
    if (files.length === 0) {
      this.showStatus(
        "Veuillez déposer uniquement des fichiers image",
        "error"
      );
      return;
    }
    this.handleFiles(files);
  }

  async uploadPhoto(photoFile) {
    try {
      const formData = new FormData();
      formData.append("photo", photoFile);

      const response = await fetch(
        `${this.apiBaseUrlValue}/establishments/${this.establishmentIdValue}/document_upload_sessions/${this.currentSession.session_id}/add_photo`,
        {
          method: "POST",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
          body: formData,
        }
      );

      if (response.ok) {
        const data = await response.json();
        this.currentSession.current_pages = data.current_pages;
        this.currentSession.status = data.status;
        this.capturedPhotos.push(photoFile);
        this.updateUI();
        this.addPhotoPreview(photoFile);
      } else {
        throw new Error("Failed to upload photo");
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      this.showStatus("Erreur lors de l'upload", "error");
    }
  }

  addPhotoPreview(photoFile) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const previewContainer = this.previewTarget;
      if (this.hasPreviewPlaceholderTarget) {
        this.previewPlaceholderTarget.classList.add("hidden");
      }

      if (!previewContainer.querySelector(".photo-grid")) {
        const gridContainer = document.createElement("div");
        gridContainer.className =
          "photo-grid grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6";
        previewContainer.appendChild(gridContainer);
      }

      const gridContainer = previewContainer.querySelector(".photo-grid");
      const photoElement = document.createElement("div");
      photoElement.className =
        "relative group photo-item bg-white/80 backdrop-blur-sm border-2 border-primary/20 rounded-2xl overflow-hidden hover:bg-white hover:shadow-lg hover:shadow-primary/10 hover:border-primary/60 hover:scale-[1.02] transition-all duration-500";
      photoElement.setAttribute(
        "data-photo-index",
        this.capturedPhotos.length - 1
      );
      photoElement.innerHTML = `
        <div class="aspect-square overflow-hidden">
          <img src="${e.target.result}" alt="Photo capturée" class="w-full h-full object-cover">
        </div>
        <div class="absolute top-3 right-3 bg-primary/90 text-white text-xs px-3 py-1 rounded-full font-semibold shadow-lg">
          ${this.capturedPhotos.length}
        </div>
        <div class="absolute top-3 left-3 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button type="button" class="btn btn-error btn-sm btn-circle shadow-lg" aria-label="Supprimer cette page" data-action="click->document-upload-session#removePhoto" data-photo-index="${this.capturedPhotos.length - 1}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="absolute bottom-3 left-3 right-3 flex justify-between opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button type="button" class="btn btn-info btn-sm btn-circle shadow-lg" aria-label="Monter la page" data-action="click->document-upload-session#movePhotoUp" data-photo-index="${this.capturedPhotos.length - 1}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
            </svg>
          </button>
          <button type="button" class="btn btn-info btn-sm btn-circle shadow-lg" aria-label="Descendre la page" data-action="click->document-upload-session#movePhotoDown" data-photo-index="${this.capturedPhotos.length - 1}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
        </div>
      `;
      gridContainer.appendChild(photoElement);
    };
    reader.readAsDataURL(photoFile);
  }

  async removePhoto(event) {
    const photoIndex = parseInt(
      event.target.closest("[data-photo-index]").dataset.photoIndex
    );
    
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette photo ?")) {
      return;
    }

    try {
      // Call backend to remove photo from session
      const response = await fetch(
        `${this.apiBaseUrlValue}/establishments/${this.establishmentIdValue}/document_upload_sessions/${this.currentSession.session_id}/remove_photo?photo_index=${photoIndex}`,
        {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Update local state
        this.capturedPhotos.splice(photoIndex, 1);
        this.rebuildPhotoGrid();
        this.updateUI();
        this.showStatus("Photo supprimée avec succès", "success");
      } else {
        throw new Error("Failed to remove photo from server");
      }
    } catch (error) {
      console.error("Error removing photo:", error);
      this.showStatus("Erreur lors de la suppression de la photo", "error");
    }
  }

  movePhotoUp(event) {
    const photoIndex = parseInt(
      event.target.closest("[data-photo-index]").dataset.photoIndex
    );
    if (photoIndex > 0) {
      [this.capturedPhotos[photoIndex - 1], this.capturedPhotos[photoIndex]] = [
        this.capturedPhotos[photoIndex],
        this.capturedPhotos[photoIndex - 1],
      ];
      this.rebuildPhotoGrid();
      this.showStatus("Ordre des photos mis à jour", "success");
    }
  }

  movePhotoDown(event) {
    const photoIndex = parseInt(
      event.target.closest("[data-photo-index]").dataset.photoIndex
    );
    if (photoIndex < this.capturedPhotos.length - 1) {
      [this.capturedPhotos[photoIndex], this.capturedPhotos[photoIndex + 1]] = [
        this.capturedPhotos[photoIndex + 1],
        this.capturedPhotos[photoIndex],
      ];
      this.rebuildPhotoGrid();
      this.showStatus("Ordre des photos mis à jour", "success");
    }
  }

  updatePhotoIndices() {
    const photoItems = this.previewTarget.querySelectorAll(".photo-item");
    photoItems.forEach((item, index) => {
      item.setAttribute("data-photo-index", index);
      const numberBadge = item.querySelector(".absolute.top-3.right-3"); // Updated selector
      if (numberBadge) {
        numberBadge.textContent = index + 1;
      }
      const buttons = item.querySelectorAll("[data-photo-index]");
      buttons.forEach((button) => {
        button.setAttribute("data-photo-index", index);
      });
    });
  }

  rebuildPhotoGrid() {
    const gridContainer = this.previewTarget.querySelector(".photo-grid");
    if (!gridContainer) {
      if (this.capturedPhotos.length === 0 && this.hasPreviewPlaceholderTarget) {
        this.previewPlaceholderTarget.classList.remove("hidden");
      }
      return;
    }
    gridContainer.innerHTML = ""; // Clear grid
    this.capturedPhotos.forEach((photo, index) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const photoElement = document.createElement("div");
        photoElement.className =
          "relative group photo-item bg-white/80 backdrop-blur-sm border-2 border-primary/20 rounded-2xl overflow-hidden hover:bg-white hover:shadow-lg hover:shadow-primary/10 hover:border-primary/60 hover:scale-[1.02] transition-all duration-500"; // Applied styling
        photoElement.setAttribute("data-photo-index", index);
        photoElement.innerHTML = `
          <div class="aspect-square overflow-hidden">
            <img src="${e.target.result}" alt="Photo capturée" class="w-full h-full object-cover">
          </div>
          <div class="absolute top-3 right-3 bg-primary/90 text-white text-xs px-3 py-1 rounded-full font-semibold shadow-lg">
            ${index + 1}
          </div>
          <div class="absolute top-3 left-3 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            <button type="button" class="btn btn-error btn-sm btn-circle shadow-lg" aria-label="Supprimer cette page" data-action="click->document-upload-session#removePhoto" data-photo-index="${index}">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="absolute bottom-3 left-3 right-3 flex justify-between opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            <button type="button" class="btn btn-info btn-sm btn-circle shadow-lg" aria-label="Monter la page" data-action="click->document-upload-session#movePhotoUp" data-photo-index="${index}">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
              </svg>
            </button>
            <button type="button" class="btn btn-info btn-sm btn-circle shadow-lg" aria-label="Descendre la page" data-action="click->document-upload-session#movePhotoDown" data-photo-index="${index}">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
          </div>
        `;
        gridContainer.appendChild(photoElement);
      };
      reader.readAsDataURL(photo);
    });
  }

  async completeDocument() {
    if (!this.currentSession || this.capturedPhotos.length === 0) return;

    try {
      this.showStatus("Démarrage du traitement...");
      
      // Disable all buttons during processing
      this.disableAllButtons();

      const response = await fetch(
        `${this.apiBaseUrlValue}/establishments/${this.establishmentIdValue}/document_upload_sessions/${this.currentSession.session_id}/complete_document`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        // Start polling for completion
        this.showStatus("Traitement en cours...");
        this.pollForCompletion(data.session_id);
      } else {
        const errorData = await response.json();
        // Re-enable buttons on error
        this.updateUI();
        throw new Error(errorData.error || "Failed to start document processing");
      }
    } catch (error) {
      console.error("Error completing document:", error);
      this.showStatus(`Erreur: ${error.message}`, "error");
      // Re-enable buttons on error
      this.updateUI();
    }
  }

  async pollForCompletion(sessionId) {
    const maxAttempts = 60; // Poll for up to 2 minutes
    let attempts = 0;

    const poll = async () => {
      attempts++;
      
      try {
        const response = await fetch(
          `${this.apiBaseUrlValue}/establishments/${this.establishmentIdValue}/document_upload_sessions/${sessionId}/status`,
          {
            headers: {
              "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
                .content,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();

          if (data.status === "completed" && data.document_id) {
            window.location.href = `/establishments/${this.establishmentIdValue}/documents/${data.document_id}?upload_success=1`;
          } else if (data.status === "failed") {
            this.showStatus(
              `Erreur lors du traitement: ${data.error || "Erreur inconnue"}`,
              "error"
            );
          } else if (data.status === "processing") {
            if (attempts < maxAttempts) {
              // Adaptive polling: fast initially (500ms), then slower (2s)
              const pollInterval = attempts <= 5 ? 500 : 2000;
              setTimeout(poll, pollInterval);
              
              // Only update status on first poll to avoid flickering
              if (attempts === 1) {
                const photoCount = data.photos_count || this.capturedPhotos.length;
                this.showStatus(`Fusion de ${photoCount} photos en PDF...`);
              }
            } else {
              this.showStatus(
                "Le traitement prend plus de temps que prévu. Veuillez vérifier vos documents.",
                "warning"
              );
            }
          }
        } else {
          throw new Error("Failed to get session status");
        }
      } catch (error) {
        console.error("Error polling for completion:", error);
        this.showStatus("Erreur lors de la vérification du statut", "error");
      }
    };

    // Start polling immediately
    poll();
  }

  // UI Updates
  updateUI() {
    if (!this.currentSession) {
      this.hideAllButtons();
      return;
    }

    const hasPhotos = this.capturedPhotos.length > 0;
    const canCapture =
      this.currentSession.status === "pending" ||
      this.currentSession.status === "capturing";
    const canComplete =
      hasPhotos &&
      (this.currentSession.status === "pending" ||
        this.currentSession.status === "capturing" ||
        this.currentSession.status === "processing");

    // Update capture button
    if (this.hasCaptureButtonTarget) {
      this.captureButtonTarget.disabled = !canCapture;
      // Remove disabled styling classes
      if (canCapture) {
        this.captureButtonTarget.classList.remove("opacity-50", "cursor-not-allowed");
      }
    }

    // Update complete button
    if (this.hasCompleteButtonTarget) {
      this.completeButtonTarget.disabled = !canComplete;
      this.completeButtonTarget.style.display = canComplete ? "flex" : "none";
      // Remove disabled styling classes
      if (canComplete) {
        this.completeButtonTarget.classList.remove("opacity-50", "cursor-not-allowed");
      }
    }

    // Update progress with user-friendly messages
    if (this.hasProgressTarget) {
      if (this.capturedPhotos.length === 0) {
        this.progressTarget.textContent = "Aucune photo ajoutée";
      } else if (this.capturedPhotos.length === 1) {
        this.progressTarget.textContent = "1 photo ajoutée";
      } else {
        this.progressTarget.textContent = `${this.capturedPhotos.length} photos ajoutées`;
      }
    }

    // Show/hide drag drop area based on photo count
    if (this.hasDragDropAreaTarget) {
      this.dragDropAreaTarget.style.display =
        this.capturedPhotos.length === 0 ? "block" : "none";
      // Re-enable drag-drop area and remove disabled styling
      this.dragDropAreaTarget.style.pointerEvents = "";
      this.dragDropAreaTarget.classList.remove("opacity-50");
    }

    // Update capture button text based on mobile detection
    if (this.hasCaptureButtonTarget) {
      const buttonText = this.captureButtonTarget.querySelector("span");
      if (buttonText) {
        if (this.isMobile()) {
          buttonText.textContent = "📷 Prendre une photo";
        } else {
          buttonText.textContent = "Ajouter des photos";
        }
      }
    }
  }

  hideAllButtons() {
    if (this.hasCaptureButtonTarget)
      this.captureButtonTarget.style.display = "none";
    if (this.hasCompleteButtonTarget)
      this.completeButtonTarget.style.display = "none";
    if (this.hasCancelButtonTarget)
      this.cancelButtonTarget.style.display = "none";
  }

  disableAllButtons() {
    if (this.hasCaptureButtonTarget) {
      this.captureButtonTarget.disabled = true;
      this.captureButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    }
    if (this.hasCompleteButtonTarget) {
      this.completeButtonTarget.disabled = true;
      this.completeButtonTarget.classList.add("opacity-50", "cursor-not-allowed");
    }
    // Also disable the drag-drop area
    if (this.hasDragDropAreaTarget) {
      this.dragDropAreaTarget.style.pointerEvents = "none";
      this.dragDropAreaTarget.classList.add("opacity-50");
    }
  }

  showStatus(message, type = "info") {
    if (!this.hasStatusTarget) return;

    const statusElement = this.statusTarget;
    const textSpan = statusElement.querySelector("span");
    const isLoading =
      type === "info" && (message.includes("...") || message.includes("cours"));
    const isReady =
      type === "success" ||
      message === "Prêt" ||
      message.startsWith("Prêt");

    if (textSpan) {
      textSpan.textContent = message;
    }

    statusElement.className =
      "inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm";

    if (isReady) {
      statusElement.classList.add("bg-success/10", "text-success");
    } else {
      switch (type) {
        case "success":
          statusElement.classList.add("bg-success/10", "text-success");
          break;
        case "error":
          statusElement.classList.add("bg-error/10", "text-error");
          break;
        case "warning":
          statusElement.classList.add("bg-warning/10", "text-warning");
          break;
        default:
          statusElement.classList.add("bg-primary/10", "text-primary");
      }
    }

    if (this.hasStatusSpinnerTarget) {
      this.statusSpinnerTarget.classList.toggle("hidden", !isLoading);
    }
    if (this.hasStatusIconTarget) {
      this.statusIconTarget.classList.toggle("hidden", isLoading);
    }
  }

  async fetchSession(sessionId) {
    return fetch(
      `${this.apiBaseUrlValue}/establishments/${this.establishmentIdValue}/document_upload_sessions/${sessionId}`,
      {
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            .content,
        },
      }
    ).then((response) => response.json());
  }

  isMobile() {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
    );
  }

  hasCameraSupport() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }
}
